import { useState } from "react";
import { Modal } from "@/components/base/Modal";
import { MoneyInput } from "@/components/base/MoneyInput";
import { currencySymbol } from "@/lib/currency";
import { moneyInputDisplay } from "@/lib/money-entry";
import type { PriceStrategy } from "@/lib/channels";
import type { PriceStrategyConfig } from "@/lib/listing-pricing";
import type { VariantChannelPriceRow } from "@/lib/listing-channel-price-display";
import {
  confirmClearListingOverrideCopy,
  confirmSaveListingOverrideCopy,
  listingChannelOverrideControlsVisible,
  parseListingChannelOverrideInput,
  resultingEffectiveAfterOverrideChange,
} from "@/lib/listing-variant-channel-price-control";

type ConfirmState = {
  kind: "save" | "clear";
  title: string;
  body: string;
  confirmLabel: string;
  nextPrice: number | null;
};

export function VariantChannelPriceControl(props: {
  row: VariantChannelPriceRow;
  role: string | null | undefined;
  currency?: string | null;
  channelName: string;
  priceStrategy: PriceStrategy;
  pricingConfig?: PriceStrategyConfig;
  busy: boolean;
  onCommit: (variantId: string, price: number | null) => Promise<void>;
  onError: (message: string) => void;
}) {
  const { row } = props;
  const visible = listingChannelOverrideControlsVisible(props.role, row.variantId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  if (!visible) return null;

  const openEdit = () => {
    setDraft(moneyInputDisplay(row.listingOverride));
    setEditing(true);
  };

  const askSave = () => {
    const parsed = parseListingChannelOverrideInput(draft);
    if (parsed.ok === false) {
      props.onError(parsed.error);
      return;
    }
    const copy = confirmSaveListingOverrideCopy({
      options: row.options,
      currentEffective: row.effective,
      newPrice: parsed.price,
      channelName: props.channelName,
      currency: props.currency,
    });
    setConfirm({ kind: "save", ...copy, nextPrice: parsed.price });
  };

  const askClear = () => {
    const resulting = resultingEffectiveAfterOverrideChange({
      priceStrategy: props.priceStrategy,
      pricingConfig: props.pricingConfig,
      canonicalBase: row.canonicalBase,
      channelPrice: null,
    });
    const copy = confirmClearListingOverrideCopy({
      options: row.options,
      currentOverride: row.listingOverride ?? row.effective ?? 0,
      resultingEffective: resulting,
      channelName: props.channelName,
      currency: props.currency,
    });
    setConfirm({ kind: "clear", ...copy, nextPrice: null });
  };

  const commit = async () => {
    if (!confirm) return;
    try {
      await props.onCommit(row.variantId, confirm.nextPrice);
      setConfirm(null);
      setEditing(false);
    } catch (err) {
      props.onError((err as Error).message);
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {!editing && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="text-xs font-medium text-emerald-700"
            onClick={openEdit}
          >
            {row.kind === "override" ? "Edit override" : "Edit channel price"}
          </button>
          {row.kind === "override" && (
            <button type="button" className="text-xs font-medium text-slate-600" onClick={askClear}>
              Use channel rule
            </button>
          )}
        </div>
      )}
      {editing && (
        <div className="max-w-xs space-y-2">
          <MoneyInput
            label="Channel price"
            symbol={currencySymbol(props.currency)}
            value={draft}
            onChange={(raw) => setDraft(raw)}
            placeholder="1.00"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={props.busy}
              onClick={askSave}
              className="px-3 py-1.5 bg-emerald-600 disabled:opacity-60 text-white text-xs font-medium rounded-lg"
            >
              Save override
            </button>
            {row.kind === "override" && (
              <button
                type="button"
                disabled={props.busy}
                onClick={askClear}
                className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-medium rounded-lg"
              >
                Use channel rule
              </button>
            )}
            <button
              type="button"
              className="px-3 py-1.5 text-xs font-medium text-slate-500"
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title ?? ""}
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={props.busy}
              onClick={() => void commit()}
              className="px-4 py-2 bg-emerald-600 disabled:opacity-60 text-white text-sm font-medium rounded-lg"
            >
              {props.busy ? "Saving..." : confirm?.confirmLabel}
            </button>
          </>
        }
      >
        <p className="text-sm text-slate-600 whitespace-pre-line">{confirm?.body}</p>
      </Modal>
    </div>
  );
}
